import { useEffect, useState } from 'react';
import { Property } from '@/types/property';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default Leaflet icon
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

// Custom circle marker icon
const createCircleIcon = (color: string = '#2563eb') => {
  return L.divIcon({
    className: 'custom-div-icon',
    html: `<div style="background-color: ${color}; width: 20px; height: 20px; border-radius: 50%; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>`,
    iconSize: [20, 20],
    iconAnchor: [10, 10],
    popupAnchor: [0, -10]
  });
};

interface PropertyMapProps {
  properties: Property[];
  onMapLoad?: (map: L.Map) => void;
  highlightedProperty?: string | null;
  interactive?: boolean;
}

// Component to handle map center and bounds
function MapController({ properties, highlightedProperty, onMapLoad }: { 
  properties: Property[], 
  highlightedProperty?: string | null,
  onMapLoad?: (map: L.Map) => void 
}) {
  const map = useMap();

  useEffect(() => {
    if (onMapLoad) {
      onMapLoad(map);
    }
  }, [map, onMapLoad]);

  useEffect(() => {
    if (highlightedProperty) {
      const property = properties.find(p => (p._id === highlightedProperty || p.id === highlightedProperty));
      if (property?.location?.coordinates) {
        map.setView([property.location.coordinates.lat, property.location.coordinates.lng], 15);
      }
    } else if (properties.length > 0) {
      const bounds = L.latLngBounds(properties
        .filter(p => p.location?.coordinates?.lat && p.location?.coordinates?.lng)
        .map(p => [p.location.coordinates.lat, p.location.coordinates.lng] as [number, number])
      );
      if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
      }
    }
  }, [properties, highlightedProperty, map]);

  return null;
}

export function PropertyMap({ 
  properties, 
  onMapLoad, 
  highlightedProperty,
  interactive = true 
}: PropertyMapProps) {
  const navigate = useNavigate();

  const getPropertyId = (p: Property) => p._id || p.id || '';

  return (
    <div className="w-full h-full rounded-lg overflow-hidden border-2 border-ink shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] relative z-0">
      <MapContainer 
        center={[8.0, -66.0]} 
        zoom={6} 
        scrollWheelZoom={interactive}
        style={{ height: '100%', width: '100%' }}
        className="z-0"
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        <MapController 
          properties={properties} 
          highlightedProperty={highlightedProperty} 
          onMapLoad={onMapLoad} 
        />

        {properties.map((property) => {
          const coords = property.location?.coordinates;
          if (!coords?.lat || !coords?.lng) return null;

          const propertyId = getPropertyId(property);
          const isHighlighted = highlightedProperty === propertyId;

          return (
            <Marker 
              key={propertyId} 
              position={[coords.lat, coords.lng]}
              icon={createCircleIcon(isHighlighted ? '#D94E33' : '#2563eb')}
            >
              <Popup className="brutalist-popup">
                <div 
                  className="property-popup cursor-pointer w-[240px]"
                  onClick={() => navigate(`/propiedades/${propertyId}`)}
                >
                  <div className="relative h-32 overflow-hidden mb-2 border-b border-ink/10">
                    <img 
                      src={property.images?.[0] || 'https://via.placeholder.com/300x200?text=No+Image'} 
                      alt={property.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-2 right-2 bg-primary/90 text-white px-2 py-1 text-xs font-bold rounded">
                      ${property.price?.toLocaleString()}
                    </div>
                  </div>
                  <h3 className="font-bold text-sm truncate mb-1">{property.title}</h3>
                  <p className="text-xs text-gray-500 truncate mb-2">{property.location?.address}</p>
                  <div className="flex gap-2 text-[10px] text-gray-600 font-medium">
                    <span>{property.bedrooms || property.beds || 0} hab</span>
                    <span>•</span>
                    <span>{property.bathrooms || property.baths || 0} baños</span>
                    <span>•</span>
                    <span>{property.area || property.sqft || 0} m²</span>
                  </div>
                  {interactive && (
                    <button 
                      className="w-full mt-3 py-2 bg-ink text-parchment text-xs font-black uppercase tracking-widest hover:bg-terracotta transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/propiedades/${propertyId}`);
                      }}
                    >
                      Ver Propiedad
                    </button>
                  )}
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>

      {/* Global CSS for Leaflet Popups to match Brutalist Style */}
      <style dangerouslySetInnerHTML={{ __html: `
        .leaflet-popup-content-wrapper {
          border-radius: 0 !important;
          padding: 0 !important;
          border: 2px solid #0F0F0F !important;
          box-shadow: 4px 4px 0px 0px rgba(0,0,0,1) !important;
          background-color: #F5F2ED !important;
        }
        .leaflet-popup-content {
          margin: 0 !important;
          width: auto !important;
        }
        .leaflet-popup-tip {
          background-color: #0F0F0F !important;
        }
        .brutalist-popup .leaflet-popup-content {
          padding: 0 !important;
        }
      `}} />
    </div>
  );
}
